/**
 * MultiVendorBadge — the canonical-item flag on a count card: a compact
 * neutral pill saying "this one countable item has several vendor products
 * grouped under it", with a panel that reviews them.
 *
 * Behavior:
 *   - Renders null unless the item is CANONICAL — ≥2 active purchase options,
 *     from one vendor or several (see utils/itemCostSummary#canonicalLinkage).
 *     A lone option → nothing; the host keeps its plain vendor text.
 *   - The pill reads "Multi-vendor" when there is a real vendor choice, and
 *     "N linked products" when one vendor supplies several packs — different
 *     situations, different words.
 *   - The panel shows each connected product with its unit price (per the
 *     item's count unit), its comparable price in the relationship's one
 *     comparison unit (utils/priceComparison — the same view-model every
 *     surface renders from), pack, purchase unit, SKU, MOQ, and price date.
 *     The lowest comparable price wears the tag; an estimated one says so and
 *     by how much it is trusted; an unresolved one says 'Needs review' rather
 *     than showing a number.
 *   - Desktop: hovering the pill opens the detail popover after a small
 *     hover-intent delay (150ms, so merely sweeping the cursor across the row
 *     doesn't pop it); moving the pointer away closes it again. Clicking the
 *     pill pins the popover open until it's clicked again, the × is pressed,
 *     or the user clicks anywhere outside.
 *   - Mobile (mobileTool): no hover — tapping the pill opens a bottom-sheet
 *     panel (portaled to document.body) with a dimmed overlay.
 *
 * The pill is informational chrome: a small gray pill, never a warning color,
 * and every control stops propagation so taps never reach the count
 * input/keypad handlers on the surrounding row.
 *
 * Props:
 *   options             - purchase_options rows for this item (any status)
 *   itemName            - canonical item name shown at the top of the panel
 *   countUnit?          - item unit (e.g. 'gal'); unit prices are shown per it
 *   item?               - inventory item row; its comparison_* columns feed
 *                         the stored relationship comparison. Optional — a
 *                         unanimous v2 snapshot unit resolves without it.
 *   mobileTool?         - true → bottom-sheet trigger instead of hover popover
 *   onViewDetails?      - "View item details" action (host opens the item modal)
 *   onManage?           - "Review connected items" action (host opens the editor)
 *   onPreferredChanged? - called after a successful "Set as preferred" so the
 *                         host can refetch this item's options
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { makePreferred } from '../services/purchaseOptionService';
import {
  canonicalLinkage,
  comparableUnitCost,
  formatUnitPrice,
} from '../utils/itemCostSummary';
import { buildComparablePriceDisplay } from '../utils/priceComparison';

// Hover-intent delay (ms): pointer must rest on the pill this long before the
// popover opens on hover alone. Clicking always opens immediately.
const HOVER_INTENT_MS = 150;

const vendorKeyOf = (option) =>
  option?.vendor_id ?? option?.vendor_description ?? null;

/**
 * Count of DISTINCT vendors across an item's ACTIVE purchase options.
 * Exported so hosts can decide between this badge and their legacy
 * single-vendor text without duplicating the filter.
 */
export function countDistinctActiveVendors(options) {
  const vendors = new Set(
    (options || [])
      .filter((o) => o?.status === 'active')
      .map(vendorKeyOf)
      .filter((key) => key !== null && key !== undefined && key !== ''),
  );
  return vendors.size;
}

const money = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? `$${parsed.toFixed(2)}` : '—';
};

const formatDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString();
};

const styles = {
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    padding: '2px 8px',
    borderRadius: 999,
    background: '#F5F0EB',
    color: '#5A4A42',
    border: '1px solid #D9CEC2',
    fontSize: '0.66rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    fontFamily: 'inherit',
    lineHeight: 1.5,
    flexShrink: 0,
  },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    zIndex: 10001,
    width: 300,
    maxWidth: '82vw',
    background: '#FEFCFA',
    border: '1px solid #E8DDD0',
    borderRadius: 12,
    boxShadow: '0 10px 30px rgba(62,39,35,0.22)',
    padding: '12px 12px 10px',
    textAlign: 'left',
    cursor: 'default',
  },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
  itemName: { fontWeight: 700, fontSize: 13, color: '#3E2723', lineHeight: 1.3 },
  vendorCount: { fontSize: 11, color: '#8B7B73', marginTop: 2, marginBottom: 8 },
  closeBtn: {
    background: 'none', border: 'none', fontSize: 16, lineHeight: 1, cursor: 'pointer',
    color: '#8B7B73', padding: '0 2px', flexShrink: 0,
  },
  optionCard: (isPreferred) => ({
    border: `1px solid ${isPreferred ? '#E6B866' : '#EDE4DA'}`,
    borderRadius: 9,
    padding: '7px 9px',
    marginBottom: 6,
    background: isPreferred ? '#FFFBF2' : '#fff',
  }),
  optionTop: { display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' },
  vendorName: { fontWeight: 700, fontSize: 12, color: '#3D2518' },
  star: { color: '#E6A817', fontSize: 12 },
  lowestTag: {
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
    background: '#E8F5E9', color: '#2E7D32', borderRadius: 999, padding: '1px 6px',
    whiteSpace: 'nowrap',
  },
  estimatedTag: {
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
    background: '#FFF4E0', color: '#8A5A00', borderRadius: 999, padding: '1px 6px',
    whiteSpace: 'nowrap',
  },
  optionMeta: { fontSize: 11, color: '#5A4A42', marginTop: 3 },
  optionPrice: { fontSize: 11, color: '#8B7B73', marginTop: 2 },
  optionUnitPrice: { fontSize: 11.5, color: '#3D2518', fontWeight: 700, marginTop: 2 },
  optionComparable: { fontSize: 11.5, color: '#3D2518', fontWeight: 700, marginTop: 2 },
  optionComparableNote: { fontSize: 10.5, color: '#8A5A00', marginTop: 2 },
  setPreferredBtn: {
    marginTop: 6, padding: '3px 9px', borderRadius: 7, fontSize: 10.5, fontWeight: 700,
    background: '#FFF4E0', color: '#8A5A00', border: '1px solid #E6B866', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  footerActions: { display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' },
  actionBtn: {
    flex: 1, minHeight: 30, padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
    background: '#fff', color: '#5A4A42', border: '1.5px solid #D5C9C0', cursor: 'pointer',
    fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(62,39,35,0.45)', zIndex: 10002,
  },
  sheet: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10003,
    background: '#FEFCFA', borderRadius: '16px 16px 0 0', border: '1px solid #E8DDD0',
    borderBottom: 'none', boxShadow: '0 -8px 30px rgba(0,0,0,0.25)',
    padding: '14px 16px calc(14px + env(safe-area-inset-bottom, 0px))',
    maxHeight: '72vh', overflowY: 'auto',
  },
  sheetHandle: {
    width: 36, height: 4, borderRadius: 2, background: '#D9CEC2', margin: '0 auto 10px',
  },
};

export default function MultiVendorBadge({
  options,
  itemName,
  countUnit,
  item = null,
  mobileTool = false,
  onViewDetails,
  onManage,
  onPreferredChanged,
  // Members in the item's working-set relationship group, itself included.
  // Under link-don't-merge the multi-vendor reality lives on SIBLING items,
  // so group membership qualifies the badge even when this item owns a
  // single option. Callers that can pool the group's options should pass
  // those pooled options too, so the panel lists every vendor.
  groupMemberCount = 0,
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pendingPreferredId, setPendingPreferredId] = useState(null);
  const hoverTimerRef = useRef(null);
  const wrapRef = useRef(null);

  const activeOptions = (options || []).filter((o) => o?.status === 'active');
  const linkage = canonicalLinkage(options, { groupMemberCount });
  const vendorCount = linkage.vendorCount;

  // The shared relationship-comparison view-model: one practical unit for
  // every card, the lowest tag, and the estimated/needs-review disclosures.
  // The badge does no unit math of its own.
  const display = buildComparablePriceDisplay({ item: item || {}, options: options || [] });
  const displayRowById = new Map(display.options.map((row) => [String(row.optionId), row]));

  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  useEffect(() => clearHoverTimer, []);

  // Clicking anywhere outside a pinned desktop popover closes it.
  useEffect(() => {
    if (!open || !pinned || mobileTool) return undefined;
    const onDocMouseDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
        setPinned(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, pinned, mobileTool]);

  if (!linkage.isCanonical) return null;

  const closePanel = () => {
    clearHoverTimer();
    setOpen(false);
    setPinned(false);
  };

  const handlePillClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (open && pinned) {
      closePanel();
    } else {
      clearHoverTimer();
      setOpen(true);
      setPinned(true);
    }
  };

  const handleMouseEnter = () => {
    if (mobileTool || open) return;
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => setOpen(true), HOVER_INTENT_MS);
  };

  const handleMouseLeave = () => {
    if (mobileTool) return;
    clearHoverTimer();
    if (!pinned) setOpen(false);
  };

  const handleSetPreferred = async (event, option) => {
    event.stopPropagation();
    if (pendingPreferredId) return;
    setPendingPreferredId(option.id);
    try {
      await makePreferred(option.id, option.inventory_item_id);
      if (onPreferredChanged) onPreferredChanged();
    } catch (err) {
      console.warn('[multi-vendor-badge] Set preferred failed:', err?.message);
    } finally {
      setPendingPreferredId(null);
    }
  };

  const handleViewDetails = (event) => {
    event.stopPropagation();
    closePanel();
    if (onViewDetails) onViewDetails();
  };

  const handleManage = (event) => {
    event.stopPropagation();
    closePanel();
    if (onManage) onManage();
  };

  const panelContent = (
    <div data-testid="mvb-panel" onClick={(e) => e.stopPropagation()}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.itemName}>{itemName || 'Item'}</div>
          <div style={styles.vendorCount}>
            {linkage.headingLabel}
            {countUnit ? ` · counted in ${countUnit}` : ''}
          </div>
        </div>
        <button type="button" aria-label="Close vendor details" style={styles.closeBtn} onClick={closePanel}>
          &times;
        </button>
      </div>
      {activeOptions.map((option) => {
        const isPreferred = option.is_preferred === true;
        const dateLabel = formatDate(option.effective_date);
        const row = displayRowById.get(String(option.id)) ?? null;
        const confidencePct = row?.estimated && Number.isFinite(Number(row.confidence))
          ? Math.round(Number(row.confidence) * 100)
          : null;
        const unitCost = comparableUnitCost(option);
        // vendor_name_text is the vendor as the source document spelled it;
        // vendor_description is the vendor's own PRODUCT name. Legacy manual
        // options put the vendor's display name in vendor_description, so it
        // stays as the fallback — but it is then not repeated as the product
        // line underneath.
        const vendorLabel = option.vendor_name_text || option.vendor_description || 'Unknown vendor';
        const productName = option.vendor_description && option.vendor_description !== vendorLabel
          ? option.vendor_description
          : null;
        return (
          <div key={option.id} data-testid="mvb-option" style={styles.optionCard(isPreferred)}>
            <div style={styles.optionTop}>
              {isPreferred && <span style={styles.star} title="Preferred vendor">★</span>}
              <span style={styles.vendorName}>{vendorLabel}</span>
              {row?.isLowest && <span style={styles.lowestTag}>Lowest comparable price</span>}
              {row?.isTiedLowest && <span style={styles.lowestTag}>Tied lowest</span>}
              {row?.estimated && <span style={styles.estimatedTag}>Estimated</span>}
            </div>
            {productName && <div style={styles.optionMeta}>{productName}</div>}
            <div style={styles.optionMeta}>
              {[
                option.pack_description || option.purchase_unit || null,
                option.qty_per_package != null && countUnit
                  ? `${option.qty_per_package} ${countUnit}/pkg`
                  : null,
                option.vendor_sku ? `SKU ${option.vendor_sku}` : null,
                option.moq_packages ? `MOQ ${option.moq_packages}` : null,
              ].filter(Boolean).join(' · ') || 'No pack details'}
            </div>
            {/* Package price and per-count-unit price are different numbers and
                are labelled as such — the cheaper package is often the dearer
                pound. */}
            <div style={styles.optionPrice}>
              {money(option.package_price)} per package
              {dateLabel ? ` · as of ${dateLabel}` : ''}
            </div>
            <div style={styles.optionUnitPrice} data-testid="mvb-unit-price">
              {unitCost === null
                ? 'Unit price unavailable — pack conversion missing'
                : formatUnitPrice(unitCost, countUnit)}
            </div>
            {/* Comparable price in the relationship's one unit — never a
                per-option re-basing. An option the comparison could not
                resolve gets words, never a number. */}
            {display.comparisonAvailable && row && (
              row.comparableDisplay ? (
                <div
                  style={styles.optionComparable}
                  data-testid="mvb-comparable"
                  title={row.assumption || undefined}
                >
                  {row.comparableDisplay}
                  {` · ${row.statusLabel}`}
                  {confidencePct != null ? ` · ${confidencePct}%` : ''}
                </div>
              ) : (
                <div style={styles.optionComparableNote} data-testid="mvb-comparable-review">
                  Needs review
                </div>
              )
            )}
            {!isPreferred && (
              <button
                type="button"
                style={styles.setPreferredBtn}
                disabled={pendingPreferredId === option.id}
                onClick={(e) => handleSetPreferred(e, option)}
              >
                {pendingPreferredId === option.id ? 'Setting…' : 'Set as preferred'}
              </button>
            )}
          </div>
        );
      })}
      {(onViewDetails || onManage) && (
        <div style={styles.footerActions}>
          {onViewDetails && (
            <button type="button" style={styles.actionBtn} onClick={handleViewDetails}>
              View item details
            </button>
          )}
          {onManage && (
            <button type="button" style={styles.actionBtn} onClick={handleManage}>
              Review connected items
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <span
      ref={wrapRef}
      style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        style={styles.pill}
        onClick={handlePillClick}
        aria-label={`${linkage.headingLabel} for ${itemName || 'this item'} — review connected items`}
      >
        {linkage.badgeLabel}
      </button>
      {!mobileTool && open && <div style={styles.popover}>{panelContent}</div>}
      {mobileTool && open && createPortal(
        <>
          <div data-testid="mvb-sheet-overlay" style={styles.overlay} onClick={closePanel} />
          <div data-testid="mvb-sheet" role="dialog" aria-label={`Vendors for ${itemName || 'item'}`} style={styles.sheet}>
            <div style={styles.sheetHandle} />
            {panelContent}
          </div>
        </>,
        document.body,
      )}
    </span>
  );
}
